import React from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { track } from '@amplitude/analytics-browser';
import { BackLink } from '@components/BackLink/BackLink';
import { BlockReleaseDecorated } from '@data/blockReleases/types/BlockRelease.types';
import { BaseEventTrackingWrapper } from '@components/BaseEventTrackingWrapper/BaseEventTrackingWrapper';
import { TrackableEventType } from '@components/BaseEventTrackingWrapper/BaseTrackableEvents.types';
import { NextSurgeonButton } from '@components/BlockRelease/BlockReleaseNudgeCaughtUpMessage/NextSurgeonButton/NextSurgeonButton';

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'grid',
		gridTemplateColumns: '1fr 15fr',
		gridTemplateRows: 'auto',
		gridTemplateAreas: `
			"icon main main"
			". backLink nextSurgeonButton"
		`,
		rowGap: theme.spacing(2),
		borderRadius: '4px',
		alignItems: 'start',
		padding: theme.spacing(3),
		marginBottom: theme.spacing(2.5),
		width: '100%',
		boxShadow: theme.shadows[5],
		boxSizing: 'border-box',
		[theme.breakpoints.down('lg')]: {
			columnGap: theme.spacing(3),
		},
	},
	backLink: {
		marginTop: theme.spacing(1.25),
	},
	backLinkWrapper: {
		gridArea: 'backLink',
	},
	icon: {
		gridArea: 'icon',
	},
	mainSection: {
		gridArea: 'main',
	},
	message: {
		gridArea: 'main',
		fontSize: '1.5rem',
		lineHeight: '1.5',
		fontWeight: 600,
		paddingBottom: theme.spacing(2),
	},
	nextSurgeonButton: {
		gridArea: 'nextSurgeonButton',
	},
}));

interface BlockReleaseNudgeCaughtUpMessageProps {
	nextRelease: BlockReleaseDecorated;
}

const DATA_FIELD = 'blockReleaseRequestPage-blockReleaseNudgeCaughtUpMessage-backLink';

export const BlockReleaseNudgeCaughtUpMessage: React.FC<BlockReleaseNudgeCaughtUpMessageProps> = ({
	nextRelease,
}) => {
	const classes = useStyles();
	const handleTrackingEvent = React.useCallback(
		(event: React.SyntheticEvent<HTMLElement>) => {
			const eventType = event.type;
			const eventName = `link-${eventType}`;

			track(eventName, { fieldName: DATA_FIELD, eventType: eventType });
		}, []);

	return (
		<Box className={classes.root}>
			<ArrowForward className={classes.icon} color="primary" />
			<Typography className={classes.message}>
				No further recommendations for this surgeon at this time. Ready for the next surgeon&apos;s recommended block releases?
			</Typography>
			<BaseEventTrackingWrapper
				className={classes.backLinkWrapper}
				handleEvent={handleTrackingEvent}
				dataField={DATA_FIELD}
				eventsToTrack={[TrackableEventType.OnClick]}
			>
				<BackLink className={classes.backLink} navBackText="BACK TO PREVIOUS SCREEN" />
			</BaseEventTrackingWrapper>
			<NextSurgeonButton
				className={classes.nextSurgeonButton}
				nextRelease={nextRelease}
			/>
		</Box>
	);
}
